'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { ProfileFields, profileSettings } from './ProfileSettings.schema'
import {
  Button,
  FormField,
  FormLabel,
  Input,
  Modal,
  ModalAction,
  Text,
} from '@/components'
import { useTranslations } from 'next-intl'
import { appSelectors, MessageSeverity, useAppStore } from '@/store'
import { apiClient } from '@/config'
import { cn } from '@/lib/utils'

export const ProfileSettings: React.FC = () => {
  const t = useTranslations('profile')
  const { user } = useAppStore(appSelectors.auth)
  const [isDeleteInProgress, setIsDeleteInProgress] = useState<boolean>(false)
  const { setMessage } = useAppStore(appSelectors.actions)
  const { fetchUser } = useAppStore(appSelectors.asyncActions)

  const form = useForm({
    mode: 'onSubmit',
    shouldUnregister: true,
    resolver: yupResolver(profileSettings),
  })

  const { control, handleSubmit, reset } = form

  const { password } = useWatch({
    control,
  })

  const onSubmit = () => {
    handleSubmit(
      async (data) => {
        if (user?.userId) {
          try {
            const password =
              data?.password &&
              data?.passwordConfirm &&
              data.password.localeCompare(data.passwordConfirm) > -1
                ? data.password
                : undefined

            await apiClient.patchUpdateData({
              email: data.email,
              name: data.username,
              password,
            })

            setMessage({
              message: t('result.success'),
              severity: MessageSeverity.SUCCESS,
            })

            await fetchUser()
          } catch (err) {
            console.error(err)
            setMessage({
              message: t('result.failed'),
              severity: MessageSeverity.ERROR,
            })
          }
        }
      },
      (err) => {
        console.log(err)
        const errors = Object.entries(err)
          .map(
            ([key, fieldErr]) =>
              `${t(`fields.${key}`)}: ${t(fieldErr.message as string)}`,
          )
          .join(', ')
        setMessage({
          message: errors,
          severity: MessageSeverity.ERROR,
        })
      },
    )()
  }

  const onDeleteProfile = async () => {
    if (user?.userId) {
      try {
        await apiClient.deleteUserWithId(user.userId)

        setMessage({
          message: t('delete.result.success'),
          severity: MessageSeverity.SUCCESS,
        })
      } catch (err) {
        console.error(err)
        setMessage({
          message: t('delete.result.failed'),
          severity: MessageSeverity.ERROR,
        })
      }
    }
  }

  useEffect(() => {
    if (user) {
      reset({
        [ProfileFields.email]: user.email,
        [ProfileFields.username]: user.name,
        password: '',
        passwordConfirm: '',
      })
    }
  }, [reset, user])

  return (
    <div
      className={cn(
        'w-full h-full flex-1',
        'bg-waves relative',
        'w-full h-full bg-no-repeat bg-cover bg-local pt-6 bg-position-[top_300px_center]',
      )}
    >
      <div className="xl:max-w-2xl py-8 px-12 border border-white/15 rounded-xl text-white mx-auto mt-24 mb-24">
        <Text className="mb-12" variant="heading2Xl">
          {t('title')}
        </Text>

        <FormProvider {...form}>
          <FormField
            control={control}
            name={ProfileFields.email}
            render={({ field }) => (
              <div className="flex flex-col gap-3 flex-1">
                <FormLabel>{t(`fields.${ProfileFields.email}`)}</FormLabel>
                <Input
                  {...field}
                  className="rounded-full border-white/20 mb-5"
                />
              </div>
            )}
          />

          <FormField
            control={control}
            name={ProfileFields.username}
            render={({ field }) => (
              <div className="flex flex-col gap-3 flex-1">
                <FormLabel>{t(`fields.${ProfileFields.username}`)}</FormLabel>
                <Input
                  {...field}
                  className="rounded-full border-white/20 mb-5"
                />
              </div>
            )}
          />

          <FormField
            control={control}
            name={ProfileFields.password}
            render={({ field }) => (
              <div className="flex flex-col gap-3 flex-1">
                <FormLabel>{t(`fields.${ProfileFields.password}`)}</FormLabel>
                <Input
                  type="password"
                  {...field}
                  className="rounded-full border-white/20 mb-5"
                />
              </div>
            )}
          />

          <FormField
            control={control}
            name={ProfileFields.passwordConfirm}
            render={({ field }) => (
              <div className="flex flex-col gap-3 flex-1">
                <FormLabel>
                  {t(`fields.${ProfileFields.passwordConfirm}`)}
                </FormLabel>
                <Input
                  type="password"
                  disabled={!password?.length}
                  {...field}
                  className="rounded-full border-white/20 mb-5"
                />
              </div>
            )}
          />

          <Button
            variant="outline"
            size="lg"
            onClick={onSubmit}
            className="rounded-full border-white/20"
          >
            {t('actions.save')}
          </Button>

          <div className="divider my-12 h-[1px] w-full bg-white/20" />

          <div className="flex justify-between">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setIsDeleteInProgress(true)}
              className="rounded-full border-red-500/75 text-red-500/75"
            >
              {t('actions.delete')}
            </Button>
          </div>
        </FormProvider>
      </div>
      <Modal
        open={isDeleteInProgress}
        setOpen={setIsDeleteInProgress}
        className="text-center bg-primary/40"
        actions={{
          [ModalAction.CANCEL]: {
            caption: t('delete.actions.cancel'),
            onClick: () => setIsDeleteInProgress(false),
          },
          [ModalAction.ACCEPT]: {
            caption: t('delete.actions.accept'),
            onClick: onDeleteProfile,
          },
        }}
      >
        <Text variant="heading" className="text-center">
          {t('delete.description')}
        </Text>
      </Modal>
    </div>
  )
}
